import { Component, DestroyRef, inject, input, OnInit, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgClass } from '@angular/common';
import { DocumentService } from '../../../core/services/document.service';
import { ToastService } from '../../../core/services/toast.service';
import { DocumentType } from '../../models/document.models';
import { SelectComponent, SelectOption } from '../select/select';
import { apiErrorMessage } from '../../utils/api-error';

export interface UploadedFile {
  file: File;
  name: string;
  sizeLabel: string;
  documentTypeId: string;
  status: 'pending' | 'uploading' | 'done' | 'error';
  validationError?: string;
}

@Component({
  selector: 'app-file-uploader',
  imports: [NgClass, SelectComponent],
  templateUrl: './file-uploader.html',
})
export class FileUploaderComponent implements OnInit {
  incidentId = input<string>('');
  investigationId = input<string>('');
  personId = input<string>('');
  vertical = input<boolean>(false);
  /** View-only: hides the drop zone and the Delete action. */
  readonly = input<boolean>(false);
  existingDocuments = input<{ id: string; name: string; type: string; documentUrl: string }[]>([]);
  uploaded = output<void>();
  deleted = output<void>();

  protected documentService = inject(DocumentService);
  private toast = inject(ToastService);
  private destroyRef = inject(DestroyRef);

  protected documentTypes = signal<DocumentType[]>([]);
  protected documentTypeOptions = signal<SelectOption[]>([]);
  protected uploadedFiles = signal<UploadedFile[]>([]);
  protected isDragOver = signal(false);
  protected expandedDocs = signal<Set<string>>(new Set());
  protected deletingIds = signal<Set<string>>(new Set());

  protected toggleDoc(id: string): void {
    this.expandedDocs.update((set) => {
      const next = new Set(set);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  private readonly ACCEPTED_TYPES = [
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/gif',
    'image/webp',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ];
  private readonly MAX_SIZE = 10 * 1024 * 1024;

  ngOnInit(): void {
    this.documentService
      .getDocumentTypes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (types) => {
          this.documentTypes.set(types);
          this.documentTypeOptions.set(types.map((t) => ({ value: t.id, label: t.name })));
        },
      });
  }

  protected onFileDrop(event: DragEvent): void {
    event.preventDefault();
    this.isDragOver.set(false);
    this.processFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  protected onFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.processFiles(Array.from(input.files ?? []));
    input.value = '';
  }

  protected removeFile(index: number): void {
    this.uploadedFiles.update((list) => list.filter((_, i) => i !== index));
  }

  protected onTypeSelected(index: number, typeId: string): void {
    this.uploadedFiles.update((list) =>
      list.map((f, i) => (i === index ? { ...f, documentTypeId: typeId } : f)),
    );
  }

  protected triggerUpload(index: number): void {
    this.tryUpload(index);
  }

  protected onView(documentUrl: string): void {
    if (!documentUrl) return;
    this.documentService
      .getReadUrl(documentUrl)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ next: (url) => window.open(url, '_blank', 'noopener') });
  }

  protected onDelete(id: string): void {
    if (this.readonly() || this.deletingIds().has(id)) return;
    this.deletingIds.update((s) => new Set(s).add(id));
    this.documentService
      .deleteDocument(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.deletingIds.update((s) => {
            const n = new Set(s);
            n.delete(id);
            return n;
          });
          this.toast.show('Document deleted successfully');
          this.deleted.emit();
        },
        error: (err) => {
          this.deletingIds.update((s) => {
            const n = new Set(s);
            n.delete(id);
            return n;
          });
          this.toast.show(apiErrorMessage(err, 'Failed to delete document'), 'error');
        },
      });
  }

  private tryUpload(index: number): void {
    const investigationId = this.investigationId();
    const incidentId = this.incidentId();
    const entityId = investigationId || incidentId;
    const type: 'incidents' | 'investigations' = investigationId ? 'investigations' : 'incidents';
    if (!entityId) return;

    const file = this.uploadedFiles()[index];
    if (!file || file.validationError || !file.documentTypeId || file.status !== 'pending') return;

    this.uploadedFiles.update((list) =>
      list.map((f, i) => (i === index ? { ...f, status: 'uploading' } : f)),
    );

    this.documentService
      .uploadDocument({
        entityId,
        type,
        personId: this.personId(),
        documentTypeId: file.documentTypeId,
        file: file.file,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.uploadedFiles.update((list) => list.filter((_, i) => i !== index));
          this.toast.show('Document uploaded successfully');
          this.uploaded.emit();
        },
        error: (err) => {
          this.uploadedFiles.update((list) =>
            list.map((f, i) => (i === index ? { ...f, status: 'error' } : f)),
          );
          this.toast.show(apiErrorMessage(err, 'Failed to upload document'), 'error');
        },
      });
  }

  private processFiles(files: File[]): void {
    const added: UploadedFile[] = files.map((file) => {
      if (!this.ACCEPTED_TYPES.includes(file.type))
        return {
          file,
          name: file.name,
          sizeLabel: '',
          documentTypeId: '',
          status: 'pending',
          validationError: 'File type not allowed.',
        };
      if (file.size > this.MAX_SIZE)
        return {
          file,
          name: file.name,
          sizeLabel: this.formatSize(file.size),
          documentTypeId: '',
          status: 'pending',
          validationError: 'Exceeds 10 MB limit.',
        };
      return {
        file,
        name: file.name,
        sizeLabel: this.formatSize(file.size),
        documentTypeId: '',
        status: 'pending',
      };
    });
    this.uploadedFiles.update((list) => [...list, ...added]);
  }

  private formatSize(bytes: number): string {
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
